import React, { useState, useEffect } from 'react';

function BacktestEngine({ currentModel, results, setResults }) {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dateRange, setDateRange] = useState({
    start: '2024-01-01',
    end: '2024-12-31'
  });
  const [betSize, setBetSize] = useState(100);
  const [bankroll, setBankroll] = useState(10000);

  const runBacktest = async () => {
    if (!currentModel) return;
    
    setIsRunning(true);
    setProgress(0);

    // Simulate backtest progress
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 200);

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 3000));

    // Mock results - in real implementation, this would come from your backend
    const mockResults = {
      model: currentModel,
      dateRange,
      settings: { betSize, bankroll },
      summary: {
        totalBets: 847,
        winningBets: 461,
        losingBets: 386,
        winRate: 54.4,
        totalProfit: 1847.32,
        roi: 18.5,
        maxDrawdown: -456.78,
        sharpeRatio: 1.23,
        profitFactor: 1.34
      },
      monthlyData: [
        { month: 'Jan 2024', profit: 234.56, bets: 67, winRate: 52.2 },
        { month: 'Feb 2024', profit: 156.78, bets: 71, winRate: 54.9 },
        { month: 'Mar 2024', profit: 298.45, bets: 82, winRate: 58.5 },
        { month: 'Apr 2024', profit: -89.23, bets: 76, winRate: 47.4 },
        { month: 'May 2024', profit: 187.92, bets: 69, winRate: 56.5 },
        { month: 'Jun 2024', profit: 245.67, bets: 73, winRate: 57.5 },
        { month: 'Jul 2024', profit: 123.45, bets: 78, winRate: 53.8 },
        { month: 'Aug 2024', profit: 312.78, bets: 85, winRate: 60.0 },
        { month: 'Sep 2024', profit: 167.89, bets: 72, winRate: 55.6 },
        { month: 'Oct 2024', profit: 98.34, bets: 69, winRate: 52.2 },
        { month: 'Nov 2024', profit: 201.23, bets: 74, winRate: 56.8 },
        { month: 'Dec 2024', profit: -89.52, bets: 31, winRate: 45.2 }
      ],
      variableImpact: currentModel.variables?.map(v => ({
        ...v,
        impact: Math.random() * 0.8 + 0.2, // Random impact between 0.2 and 1.0
        correlation: (Math.random() - 0.5) * 2 // Random correlation between -1 and 1
      })) || []
    };

    clearInterval(progressInterval);
    setProgress(100);
    setResults(mockResults);
    setIsRunning(false);
  };

  const getProfitColor = (profit) => {
    return profit >= 0 ? '#10b981' : '#ef4444';
  };

  const getMetricColor = (value, type) => {
    switch (type) {
      case 'winRate':
        if (value >= 55) return '#10b981';
        if (value >= 50) return '#f59e0b';
        return '#ef4444';
      case 'roi':
        if (value >= 10) return '#10b981';
        if (value >= 5) return '#f59e0b';
        return '#ef4444';
      case 'sharpe':
        if (value >= 1.5) return '#10b981';
        if (value >= 1.0) return '#f59e0b';
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  return (
    <div className="backtest-engine">
      <div className="backtest-header">
        <h2>Backtest Engine</h2>
        <p>Test your model against historical data to validate its performance</p>
      </div>

      {!currentModel ? (
        <div className="no-model">
          <div className="no-model-icon">📊</div>
          <h3>No Model Selected</h3>
          <p>Please build or select a model first before running a backtest</p>
        </div>
      ) : (
        <div className="backtest-content">
          {/* Settings Panel */}
          <div className="backtest-settings">
            <h3>Backtest Settings</h3>
            
            <div className="settings-row">
              <div className="setting-group">
                <label>Model</label>
                <div className="model-info">
                  <span className="model-name">{currentModel.name}</span>
                  <span className="variable-count">
                    {currentModel.variables?.length || 0} variables
                  </span>
                </div>
              </div>
            </div>

            <div className="settings-row">
              <div className="setting-group">
                <label>Date Range</label>
                <div className="date-inputs">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="date-input"
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="date-input"
                  />
                </div>
              </div>
            </div>

            <div className="settings-row">
              <div className="setting-group">
                <label>Bet Size ($)</label>
                <input
                  type="number"
                  value={betSize}
                  onChange={(e) => setBetSize(parseInt(e.target.value))}
                  className="number-input"
                  min="1"
                  max="1000"
                />
              </div>
              
              <div className="setting-group">
                <label>Starting Bankroll ($)</label>
                <input
                  type="number"
                  value={bankroll}
                  onChange={(e) => setBankroll(parseInt(e.target.value))}
                  className="number-input"
                  min="100"
                  max="100000"
                  step="100"
                />
              </div>
            </div>

            <button 
              className="run-backtest-btn"
              onClick={runBacktest}
              disabled={isRunning}
            >
              {isRunning ? '🔄 Running...' : '🚀 Run Backtest'}
            </button>

            {isRunning && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{Math.round(progress)}% Complete</span>
              </div>
            )}
          </div>

          {/* Results Panel */}
          {results && (
            <div className="backtest-results">
              <h3>Results</h3>
              
              {/* Summary Metrics */}
              <div className="results-summary">
                <div className="metric-card">
                  <div className="metric-icon">🎯</div>
                  <div className="metric-info">
                    <span className="metric-label">Win Rate</span>
                    <span 
                      className="metric-value"
                      style={{ color: getMetricColor(results.summary.winRate, 'winRate') }}
                    >
                      {results.summary.winRate}%
                    </span>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">💰</div>
                  <div className="metric-info">
                    <span className="metric-label">Total Profit</span>
                    <span 
                      className="metric-value"
                      style={{ color: getProfitColor(results.summary.totalProfit) }}
                    >
                      ${results.summary.totalProfit.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">📈</div>
                  <div className="metric-info">
                    <span className="metric-label">ROI</span>
                    <span 
                      className="metric-value"
                      style={{ color: getMetricColor(results.summary.roi, 'roi') }}
                    >
                      {results.summary.roi}%
                    </span>
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-icon">📊</div>
                  <div className="metric-info">
                    <span className="metric-label">Sharpe Ratio</span>
                    <span 
                      className="metric-value"
                      style={{ color: getMetricColor(results.summary.sharpeRatio, 'sharpe') }}
                    >
                      {results.summary.sharpeRatio}
                    </span>
                  </div>
                </div>
              </div>

              {/* Monthly Performance */}
              <div className="monthly-performance">
                <h4>Monthly Performance</h4>
                <div className="performance-chart">
                  {results.monthlyData.map(month => (
                    <div key={month.month} className="month-bar">
                      <div 
                        className="profit-bar"
                        style={{
                          height: `${Math.abs(month.profit) / 5}px`,
                          backgroundColor: getProfitColor(month.profit),
                          minHeight: '4px'
                        }}
                      ></div>
                      <span className="month-label">{month.month.slice(0, 3)}</span>
                      <div className="month-tooltip">
                        <div>Profit: ${month.profit.toFixed(2)}</div>
                        <div>Bets: {month.bets}</div>
                        <div>Win Rate: {month.winRate}%</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Variable Impact */}
              {results.variableImpact.length > 0 && (
                <div className="variable-impact">
                  <h4>Variable Impact Analysis</h4>
                  <div className="impact-list">
                    {results.variableImpact
                      .sort((a, b) => b.impact - a.impact)
                      .map(variable => (
                      <div key={variable.id} className="impact-item">
                        <div className="impact-info">
                          <span className="variable-name">{variable.name}</span>
                          <span className="variable-weight">{variable.weight}% weight</span>
                        </div>
                        <div className="impact-bars">
                          <div className="impact-bar">
                            <span className="impact-label">Impact</span>
                            <div className="impact-bar-bg">
                              <div 
                                className="impact-bar-fill"
                                style={{ width: `${variable.impact * 100}%` }}
                              ></div>
                            </div>
                            <span className="impact-value">{(variable.impact * 100).toFixed(0)}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BacktestEngine;
